import React, { useState } from 'react';
import { useFormik } from 'formik';
import api from '../../../../middleware/api';
import BasicInput from '../../../Common/Inputs/BasicInput/BasicInput';
import { toast } from 'react-toastify';
import usersSchema from '../../../../schema/users.schema';
import { getCountryByName, getCountryOptions, getCountryByIso } from '../../../../utils/country.utils';
import { usePortalDrawer } from '../../../Common/SidebarDrawer/DrawerPortal';
import UnifiedPhoneInput from '../../../Common/Inputs/UnifiedPhoneInput/UnifiedPhoneInput';
import { UserIcon, MapPinIcon } from '@heroicons/react/24/outline';
import ExpandableSection from '../../../Common/ExpandableSection/ExpandableSection';
import FormikCountryInput from '../../../Common/Inputs/CountryInput/FormikCountryInput';
import ProfilePictureUploader from '../../../Common/Inputs/ProfilePictureUploader/ProfilePictureUploader';

const UpdateCustomerForm = ({ customer, updateCustomer }) => {
    const { hideDrawer } = usePortalDrawer();
    const countriesOptions = getCountryOptions();
    const [profilePicture, setProfilePicture] = useState(null);

    const handleAddressCountryChange = (e) => {
        // Nous utilisons maintenant directement l'ISO du pays
        const isoCountry = e.target.value;
        formik.setFieldValue('address.isoCountry', isoCountry);
        formik.setFieldTouched('address.isoCountry', true, true);
    };

    const handleProfilePictureChange = (file) => {
        console.log("Fichier reçu:", file);
        setProfilePicture(file);
    };

    const formik = useFormik({
        initialValues: {
            firstname: customer.firstname || '',
            lastname: customer.lastname || '',
            email: customer.email || '',
            phone: customer.phone || '',
            phonePrefix: customer.phonePrefix || '',
            birthdate: customer.birthdate || '',
            contactOrigin: customer.contactOrigin || '',
            isCustomerTherapist: customer.isCustomerTherapist || false,
            address: {
                street: customer.addresses.length > 0 ? customer?.addresses[0]?.street : '',
                city: customer.addresses.length > 0 ? customer?.addresses[0]?.city : '',
                state: customer.addresses.length > 0 ? customer?.addresses[0]?.state : '',
                postcode: customer.addresses.length > 0 ? customer?.addresses[0]?.postcode : '',
                country: customer.addresses.length > 0 ? customer?.addresses[0]?.country : 'France',
                isoCountry: customer.addresses.length > 0 ? getCountryByName(customer?.addresses[0]?.country)?.iso : getCountryByName('France')?.iso,
                legalFirstname: customer.addresses.length > 0 ? customer?.addresses[0]?.legalFirstname : '',
                legalLastname: customer.addresses.length > 0 ? customer?.addresses[0]?.legalLastname : ''
            }
        },
        validationSchema: usersSchema.createCustomer,
        onSubmit: async (values, { setFieldTouched, setErrors }) => {
            try {
                values.id = customer.id;
                values.birthdate === '' ? values.birthdate = null : new Date(values.birthdate).toISOString();

                // Récupérer le nom du pays à partir de l'ISO
                if (values.address.isoCountry) {
                    const country = getCountryByIso(values.address.isoCountry);
                    if (country) {
                        values.address.country = country.name;
                    } else {
                        setFieldTouched('address.isoCountry', true, true);
                        setErrors({
                            'address.isoCountry': 'Pays invalide'
                        });
                        return;
                    }
                }

                // Create FormData to handle file upload
                const formData = new FormData();

                // Append the profile picture if exists
                if (profilePicture) {
                    console.log("Ajout de l'image au FormData:", profilePicture);
                    formData.append('file', profilePicture);
                }

                // Append user data as JSON
                formData.append('user', JSON.stringify(values));

                const response = await api.put(`/customers/update/${customer.id}`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });

                updateCustomer(response.data);
                toast.success('Client mis à jour avec succès!');
                hideDrawer();

            } catch (error) {
                console.log(error)
                toast.error('Une erreur est survenue lors de la mise à jour du client!');
            }
        },
    });

    const handleEmailChange = (e) => {
        const email = e.target.value.toLowerCase().trim();
        formik.setFieldValue('email', email);
        formik.setFieldTouched('email', true, true);
    }

    return (
        <form onSubmit={formik.handleSubmit} className='flex flex-col gap-4'>
            <h2 className='text-2xl font-bold m-0 mb-4'>Modification d'un client</h2>

            <ExpandableSection title="Informations du client" defaultExpanded={true} overflow="visible">
                <div className="grid gap-4 overflow-visible rounded-md">
                    {/* Profile Picture Uploader */}
                    <div className="flex justify-center mb-4">
                        <ProfilePictureUploader
                            onImageSelect={handleProfilePictureChange}
                            error={formik.errors.profilePicture}
                            touched={formik.touched.profilePicture}
                            defaultImage={customer.profilePictureUrl || null}
                        />
                    </div>

                    <BasicInput
                        placeholder="Prénom *"
                        name="firstname"
                        type="text"
                        value={formik.values.firstname}
                        onChange={formik.handleChange}
                        error={formik.errors.firstname}
                        touched={formik.touched.firstname}
                        icon={<UserIcon className="flex-shrink-0 flex-grow-0 h-5 w-5 text-gray-400" />}
                    />
                    <BasicInput
                        placeholder="Nom *"
                        name="lastname"
                        type="text"
                        value={formik.values.lastname}
                        onChange={formik.handleChange}
                        error={formik.errors.lastname}
                        touched={formik.touched.lastname}
                        icon={<UserIcon className="flex-shrink-0 flex-grow-0 h-5 w-5 text-gray-400" />}
                    />
                    <BasicInput
                        placeholder="Email"
                        name="email"
                        type="email"
                        value={formik.values.email}
                        onChange={handleEmailChange}
                        error={formik.errors.email}
                        touched={formik.touched.email}
                    />
                    <UnifiedPhoneInput
                        name="phone"
                        value={formik.values.phone}
                        phonePrefix={formik.values.phonePrefix}
                        onChange={formik.handleChange}
                        error={formik.errors.phone}
                        touched={formik.touched.phone}
                        placeholder="Téléphone"
                    />
                    <BasicInput
                        placeholder="Date de naissance"
                        name="birthdate"
                        type="date"
                        value={formik.values.birthdate}
                        onChange={formik.handleChange}
                        error={formik.errors.birthdate}
                        touched={formik.touched.birthdate}
                    />
                    <BasicInput
                        placeholder={"Origine de contact"}
                        name="contactOrigin"
                        type="text"
                        value={formik.values.contactOrigin}
                        onChange={formik.handleChange}
                        error={formik.errors.contactOrigin}
                        touched={formik.touched.contactOrigin}
                    />

                    <div className="form-control w-full border border-base-300 border-solid rounded-md hover:border-gray-400 transition-all duration-300 px-1">
                        <label className="label cursor-pointer space-x-4">
                            <span className="label-text text-sm font-medium">Est thérapeute</span>
                            <input
                                className='checkbox checkbox-primary checkbox-xs border border-solid'
                                type="checkbox"
                                name="isCustomerTherapist"
                                checked={formik.values.isCustomerTherapist}
                                onChange={formik.handleChange}
                            />
                        </label>
                    </div>
                </div>
            </ExpandableSection>

            <ExpandableSection title="Adresse" defaultExpanded={false} overflow="visible">
                <div className="grid gap-4 overflow-visible rounded-md">
                    <div className="text-sm text-gray-600 mb-2">
                        <span className="text-red-500">*</span> Champs obligatoires si une adresse est fournie
                    </div>
                    <BasicInput
                        placeholder="Rue *"
                        name="address.street"
                        type="text"
                        value={formik.values.address.street}
                        onChange={formik.handleChange}
                        error={formik.errors?.address?.street}
                        touched={formik.touched.address?.street}
                        icon={<MapPinIcon className="h-5 w-5 text-gray-400" />}
                    />
                    <BasicInput
                        placeholder="Code Postal *"
                        name="address.postcode"
                        type="text"
                        value={formik.values.address.postcode}
                        onChange={formik.handleChange}
                        error={formik.errors?.address?.postcode}
                        touched={formik.touched.address?.postcode}
                    />
                    <BasicInput
                        placeholder="Ville *"
                        name="address.city"
                        type="text"
                        value={formik.values.address.city}
                        onChange={formik.handleChange}
                        error={formik.errors?.address?.city}
                        touched={formik.touched.address?.city}
                    />
                    <BasicInput
                        placeholder="Région"
                        name="address.state"
                        type="text"
                        value={formik.values.address.state}
                        onChange={formik.handleChange}
                        error={formik.errors?.address?.state}
                        touched={formik.touched.address?.state}
                    />
                    <div className="w-full">
                        <FormikCountryInput
                            name="address.isoCountry"
                            value={formik.values.address.isoCountry}
                            onChange={handleAddressCountryChange}
                            error={formik.errors?.address?.isoCountry}
                            touched={formik.touched.address?.isoCountry}
                            placeholder="Pays *"
                            returnedValue="iso"
                        />

                    </div>
                    <BasicInput
                        placeholder="Prénom du Représentant Légal"
                        name="address.legalFirstname"
                        type="text"
                        value={formik.values.address.legalFirstname}
                        onChange={formik.handleChange}
                        error={formik.errors?.address?.legalFirstname}
                        touched={formik.touched.address?.legalFirstname}
                    />
                    <BasicInput
                        placeholder="Nom du Représentant Légal"
                        name="address.legalLastname"
                        type="text"
                        value={formik.values.address.legalLastname}
                        onChange={formik.handleChange}
                        error={formik.errors?.address?.legalLastname}
                        touched={formik.touched.address?.legalLastname}
                    />
                </div>
            </ExpandableSection>

            <button className='btn btn-primary w-full mt-4' type="submit">MODIFIER</button>
        </form>
    );
}

export default UpdateCustomerForm;
